import {
  saleFragment,
  voucherFragment,
  voucherTenantFragment
} from "@saleor/fragments/discounts";
import { discountErrorFragment } from "@saleor/fragments/errors";
import { pageInfoFragment } from "@saleor/fragments/pageInfo";
import makeMutation from "@saleor/hooks/makeMutation";
import gql from "graphql-tag";

import { TypedMutation } from "../mutations";
import {
  ExportSaleErrorVariants,
  ExportSaleErrorVariantsVariables
} from "./types/ExportSaleErrorVariants";
import {
  SaleBulkDeactivate,
  SaleBulkDeactivateVariables
} from "./types/SaleBulkDeactivate";
import {
  SaleBulkDelete,
  SaleBulkDeleteVariables
} from "./types/SaleBulkDelete";
import {
  SaleCataloguesAdd,
  SaleCataloguesAddVariables
} from "./types/SaleCataloguesAdd";
import {
  SaleCataloguesRemove,
  SaleCataloguesRemoveVariables
} from "./types/SaleCataloguesRemove";
import { SaleCreate, SaleCreateVariables } from "./types/SaleCreate";
import { SaleDelete, SaleDeleteVariables } from "./types/SaleDelete";
import { SaleUpdate, SaleUpdateVariables } from "./types/SaleUpdate";
import {
  UpdateSaleStatus,
  UpdateSaleStatusVariables
} from "./types/UpdateSaleStatus";
import {
  UpdateVoucherGroupStatus,
  UpdateVoucherGroupStatusVariables
} from "./types/UpdateVoucherGroupStatus";
import {
  UpdateVoucherStatus,
  UpdateVoucherStatusVariables
} from "./types/UpdateVoucherStatus";
import {
  VoucherAddBrandGroups,
  VoucherAddBrandGroupsVariables
} from "./types/VoucherAddBrandGroups";
import {
  VoucherBulkCreate,
  VoucherBulkCreateVariables
} from "./types/VoucherBulkCreate";
import {
  VoucherBulkDeactivate,
  VoucherBulkDeactivateVariables
} from "./types/VoucherBulkDeactivate";
import {
  VoucherBulkDelete,
  VoucherBulkDeleteVariables
} from "./types/VoucherBulkDelete";
import {
  VoucherCataloguesAdd,
  VoucherCataloguesAddVariables
} from "./types/VoucherCataloguesAdd";
import {
  VoucherCataloguesRemove,
  VoucherCataloguesRemoveVariables
} from "./types/VoucherCataloguesRemove";
import { VoucherCreate, VoucherCreateVariables } from "./types/VoucherCreate";
import {
  VoucherCustomersAdd,
  VoucherCustomersAddVariables
} from "./types/VoucherCustomersAdd";
import {
  VoucherCustomersRemove,
  VoucherCustomersRemoveVariables
} from "./types/VoucherCustomersRemove";
import { VoucherDelete, VoucherDeleteVariables } from "./types/VoucherDelete";
import {
  VoucherImageCreate,
  VoucherImageCreateVariables
} from "./types/VoucherImageCreate";
import {
  VoucherImageDelete,
  VoucherImageDeleteVariables
} from "./types/VoucherImageDelete";
import {
  VoucherMallsAdd,
  VoucherMallsAddVariables
} from "./types/VoucherMallsAdd";
import { VoucherMallsRemove } from "./types/VoucherMallsRemove";
import {
  VoucherTenantDelete,
  VoucherTenantDeleteVariables
} from "./types/VoucherTenantDelete";
import {
  VoucherTenantUpdate,
  VoucherTenantUpdateVariables
} from "./types/VoucherTenantUpdate";
import { VoucherUpdate, VoucherUpdateVariables } from "./types/VoucherUpdate";

const saleUpdate = gql`
  ${discountErrorFragment}
  ${saleFragment}
  mutation SaleUpdate($input: SaleInput!, $id: ID!) {
    saleUpdate(id: $id, input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      sale {
        ...SaleFragment
      }
    }
  }
`;
export const TypedSaleUpdate = TypedMutation<SaleUpdate, SaleUpdateVariables>(
  saleUpdate
);

const saleCataloguesAdd = gql`
  ${discountErrorFragment}
  mutation SaleCataloguesAdd($input: SaleCatalogueInput!, $id: ID!) {
    saleCataloguesAdd(id: $id, input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      sale {
        id
        type
      }
    }
  }
`;
export const TypedSaleCataloguesAdd = TypedMutation<
  SaleCataloguesAdd,
  SaleCataloguesAddVariables
>(saleCataloguesAdd);

const saleCataloguesRemove = gql`
  ${discountErrorFragment}
  mutation SaleCataloguesRemove($input: SaleCatalogueInput!, $id: ID!) {
    saleCataloguesRemove(id: $id, input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      sale {
        id
        type
      }
    }
  }
`;
export const TypedSaleCataloguesRemove = TypedMutation<
  SaleCataloguesRemove,
  SaleCataloguesRemoveVariables
>(saleCataloguesRemove);

const saleCreate = gql`
  ${discountErrorFragment}
  ${saleFragment}
  mutation SaleCreate($input: SaleInput!) {
    saleCreate(input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      sale {
        ...SaleFragment
      }
    }
  }
`;
export const TypedSaleCreate = TypedMutation<SaleCreate, SaleCreateVariables>(
  saleCreate
);

const saleDelete = gql`
  ${discountErrorFragment}
  mutation SaleDelete($id: ID!) {
    saleDelete(id: $id) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
    }
  }
`;
export const TypedSaleDelete = TypedMutation<SaleDelete, SaleDeleteVariables>(
  saleDelete
);

const updateSaleStatus = gql`
  ${discountErrorFragment}
  mutation UpdateSaleStatus($id: ID!, $status: DiscountStatusEnum!) {
    updateSaleStatus(id: $id, status: $status) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
    }
  }
`;
export const TypedUpdateSaleStatus = TypedMutation<
  UpdateSaleStatus,
  UpdateSaleStatusVariables
>(updateSaleStatus);
const saleBulkDelete = gql`
  mutation SaleBulkDelete($ids: [ID]!) {
    saleBulkDelete(ids: $ids) {
      errors {
        field
        message
      }
    }
  }
`;
export const TypedSaleBulkDelete = TypedMutation<
  SaleBulkDelete,
  SaleBulkDeleteVariables
>(saleBulkDelete);

const saleBulkDeactivate = gql`
  mutation SaleBulkDeactivate($ids: [ID]!) {
    saleBulkDeactivate(ids: $ids) {
      errors {
        field
        message
      }
    }
  }
`;
export const TypedsaleBulkDeactivate = TypedMutation<
  SaleBulkDeactivate,
  SaleBulkDeactivateVariables
>(saleBulkDeactivate);

const exportSaleErrorVariants = gql`
  mutation ExportSaleErrorVariants($input: ExportSaleErrorVariantsInput!) {
    exportSaleErrorVariants(input: $input) {
      exportFile {
        id
        status
        url
        message
      }
      exportErrors {
        field
        message
        code
      }
    }
  }
`;
export const useSaleErrorVariants = makeMutation<
  ExportSaleErrorVariants,
  ExportSaleErrorVariantsVariables
>(exportSaleErrorVariants);

const voucherUpdate = gql`
  ${discountErrorFragment}
  ${voucherFragment}
  mutation VoucherUpdate($input: VoucherInput!, $id: ID!) {
    voucherUpdate(id: $id, input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      voucher {
        ...VoucherFragment
      }
    }
  }
`;
export const TypedVoucherUpdate = TypedMutation<
  VoucherUpdate,
  VoucherUpdateVariables
>(voucherUpdate);

const voucherCataloguesAdd = gql`
  ${discountErrorFragment}
  ${voucherFragment}
  ${pageInfoFragment}
  mutation VoucherCataloguesAdd(
    $input: VoucherCatalogueInput!
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    voucherCataloguesAdd(id: $id, input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      voucher {
        ...VoucherFragment
        type
        code
        usageLimit
        used
        isLimitToNewCustomer
        isMinEligibleOnly
        applyOncePerOrder
        applyOncePerCustomer
        smopDiscountShare
        tenantDiscountShare
        # isAppliedToAllBrands
        # isAppliedToAllTenants
        malls(after: $after, before: $before, first: $first, last: $last) {
          edges {
            node {
              id
              name
            }
          }
        }
        collections(first: $first, last: $last) {
          edges {
            node {
              id
              name
              products {
                totalCount
              }
            }
          }
          totalCount
          pageInfo {
            ...PageInfoFragment
          }
        }
        categories(first: $first, last: $last) {
          edges {
            node {
              id
              name
              products {
                totalCount
              }
            }
          }

          totalCount
          pageInfo {
            ...PageInfoFragment
          }
        }
      }
    }
  }
`;
export const TypedVoucherCataloguesAdd = TypedMutation<
  VoucherCataloguesAdd,
  VoucherCataloguesAddVariables
>(voucherCataloguesAdd);

const voucherCataloguesRemove = gql`
  ${discountErrorFragment}
  ${voucherFragment}
  ${pageInfoFragment}
  mutation VoucherCataloguesRemove(
    $input: VoucherCatalogueInput!
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    voucherCataloguesRemove(id: $id, input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      voucher {
        ...VoucherFragment
        type
        code
        usageLimit
        used
        isLimitToNewCustomer
        isMinEligibleOnly
        applyOncePerOrder
        applyOncePerCustomer
        smopDiscountShare
        tenantDiscountShare
        malls(after: $after, before: $before, first: $first, last: $last) {
          edges {
            node {
              id
              name
            }
          }
        }
        collections(first: $first, last: $last) {
          edges {
            node {
              id
              name
              products {
                totalCount
              }
            }
          }
          totalCount
          pageInfo {
            ...PageInfoFragment
          }
        }
        categories(first: $first, last: $last) {
          edges {
            node {
              id
              name
              products {
                totalCount
              }
            }
          }

          totalCount
          pageInfo {
            ...PageInfoFragment
          }
        }
      }
    }
  }
`;
export const TypedVoucherCataloguesRemove = TypedMutation<
  VoucherCataloguesRemove,
  VoucherCataloguesRemoveVariables
>(voucherCataloguesRemove);

const voucherCreate = gql`
  ${discountErrorFragment}
  ${voucherFragment}
  mutation VoucherCreate($input: VoucherInput!) {
    voucherCreate(input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      voucher {
        ...VoucherFragment
      }
    }
  }
`;
export const TypedVoucherCreate = TypedMutation<
  VoucherCreate,
  VoucherCreateVariables
>(voucherCreate);

const voucherDelete = gql`
  ${discountErrorFragment}
  mutation VoucherDelete($id: ID!) {
    voucherDelete(id: $id) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
    }
  }
`;
export const TypedVoucherDelete = TypedMutation<
  VoucherDelete,
  VoucherDeleteVariables
>(voucherDelete);

const voucherBulkDelete = gql`
  mutation VoucherBulkDelete($ids: [ID]!) {
    voucherBulkDelete(ids: $ids) {
      errors {
        field
        message
      }
    }
  }
`;
export const TypedVoucherBulkDelete = TypedMutation<
  VoucherBulkDelete,
  VoucherBulkDeleteVariables
>(voucherBulkDelete);

const voucherBulkCreate = gql`
  mutation VoucherBulkCreate(
    $id: ID
    $multiplier: Int!
    $voucherDetails: VoucherInput
  ) {
    voucherBulkCreate(
      id: $id
      multiplier: $multiplier
      voucherDetails: $voucherDetails
    ) {
      voucherJob {
        id
        status
        processedCount
        totalCount
        voucherGroup {
          id
          code
        }
      }
      discountErrors {
        field
        message
        code
      }
    }
  }
`;
export const useVoucherBulkCreate = makeMutation<
  VoucherBulkCreate,
  VoucherBulkCreateVariables
>(voucherBulkCreate);

const updateVoucherStatus = gql`
  ${discountErrorFragment}
  mutation UpdateVoucherStatus($id: ID!, $status: DiscountStatusEnum!) {
    updateVoucherStatus(id: $id, status: $status) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
    }
  }
`;
export const TypedUpdateVoucherStatus = TypedMutation<
  UpdateVoucherStatus,
  UpdateVoucherStatusVariables
>(updateVoucherStatus);

const voucherBulkDeactivate = gql`
  mutation VoucherBulkDeactivate($ids: [ID]!) {
    voucherBulkDeactivate(ids: $ids) {
      errors {
        field
        message
      }
    }
  }
`;
export const TypedVoucherBulkDeactivate = TypedMutation<
  VoucherBulkDeactivate,
  VoucherBulkDeactivateVariables
>(voucherBulkDeactivate);

const voucherMallsAdd = gql`
  ${discountErrorFragment}
  ${pageInfoFragment}
  mutation VoucherMallsAdd(
    $input: VoucherMallInput!
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    voucherAddMalls(id: $id, input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      voucher {
        id
        type
        malls(after: $after, before: $before, first: $first, last: $last) {
          edges {
            node {
              id
              name
            }
          }
          totalCount
          pageInfo {
            ...PageInfoFragment
          }
        }
      }
    }
  }
`;
export const TypedVoucherMallsAdd = TypedMutation<
  VoucherMallsAdd,
  VoucherMallsAddVariables
>(voucherMallsAdd);

const voucherMallsRemove = gql`
  ${discountErrorFragment}
  ${pageInfoFragment}
  mutation VoucherMallsRemove(
    $input: VoucherMallInput!
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    voucherRemoveMalls(id: $id, input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      voucher {
        id
        type
        malls(after: $after, before: $before, first: $first, last: $last) {
          edges {
            node {
              id
              name
            }
          }
          totalCount
          pageInfo {
            ...PageInfoFragment
          }
        }
      }
    }
  }
`;
export const TypedVoucherMallsRemove = TypedMutation<
  VoucherMallsRemove,
  VoucherMallsAddVariables
>(voucherMallsRemove);

const voucherCustomersAdd = gql`
  ${discountErrorFragment}
  ${pageInfoFragment}
  mutation VoucherCustomersAdd(
    $input: VoucherEligibleCustomerInput!
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    voucherAddCustomers(id: $id, input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      voucher {
        id
        type
        eligibleCustomers(
          after: $after
          before: $before
          first: $first
          last: $last
        ) {
          edges {
            node {
              id
              firstName
              lastName
              email
              contactNo
            }
          }
          totalCount
          pageInfo {
            ...PageInfoFragment
          }
        }
      }
    }
  }
`;
export const TypedVoucherCustomersAdd = TypedMutation<
  VoucherCustomersAdd,
  VoucherCustomersAddVariables
>(voucherCustomersAdd);

const voucherCustomersRemove = gql`
  ${discountErrorFragment}
  ${pageInfoFragment}
  mutation VoucherCustomersRemove(
    $input: VoucherEligibleCustomerInput!
    $id: ID!
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) {
    voucherRemoveCustomers(id: $id, input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      voucher {
        id
        type
        eligibleCustomers(
          after: $after
          before: $before
          first: $first
          last: $last
        ) {
          edges {
            node {
              id
              firstName
              lastName
              email
              contactNo
            }
          }
          totalCount
          pageInfo {
            ...PageInfoFragment
          }
        }
      }
    }
  }
`;
export const TypedVoucherCustomersRemove = TypedMutation<
  VoucherCustomersRemove,
  VoucherCustomersRemoveVariables
>(voucherCustomersRemove);

export const voucherImageCreateMutation = gql`
  mutation VoucherImageCreate($voucher: ID!, $image: Upload!) {
    voucherImageCreate(input: { image: $image, voucher: $voucher }) {
      errors: discountErrors {
        field
        message
        code
      }
      voucher {
        id
        name
        imageUrl {
          url
        }
      }
    }
  }
`;
export const useVoucherImageCreateMutation = makeMutation<
  VoucherImageCreate,
  VoucherImageCreateVariables
>(voucherImageCreateMutation);

export const voucherImageDeleteMutation = gql`
  mutation VoucherImageDelete($voucher: ID!) {
    voucherImageDelete(voucher: $voucher) {
      errors: discountErrors {
        field
        message
        code
      }
      voucher {
        id
        name
        imageUrl {
          url
        }
      }
    }
  }
`;
export const useVoucherImageDeleteMutation = makeMutation<
  VoucherImageDelete,
  VoucherImageDeleteVariables
>(voucherImageDeleteMutation);

const voucherAddBrandGroups = gql`
  ${discountErrorFragment}
  mutation VoucherAddBrandGroups($input: VoucherBrandGroupsInput!, $id: ID!) {
    voucherAddBrandGroups(id: $id, input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      voucher {
        voucherTenants {
          id
          smoDiscountShare
          tenantDiscountShare
          tenant {
            tenantType
            mall {
              id
              name
            }
            brand {
              legalName
              name
              id
            }
          }
        }
      }
    }
  }
`;
export const TypedVoucherAddBrandGroups = TypedMutation<
  VoucherAddBrandGroups,
  VoucherAddBrandGroupsVariables
>(voucherAddBrandGroups);

const voucherTenantDelete = gql`
  ${discountErrorFragment}
  ${voucherTenantFragment}
  mutation VoucherTenantDelete($id: ID!) {
    voucherTenantDelete(id: $id) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      voucherTenant {
        ...VoucherTenantFragment
      }
    }
  }
`;
export const TypedVoucherTenantDelete = TypedMutation<
  VoucherTenantDelete,
  VoucherTenantDeleteVariables
>(voucherTenantDelete);

const voucherTenantUpdate = gql`
  ${discountErrorFragment}
  ${voucherTenantFragment}
  mutation VoucherTenantUpdate($id: ID!, $input: VoucherTenantSubsidyInput!) {
    voucherTenantUpdate(id: $id, input: $input) {
      errors: discountErrors {
        ...DiscountErrorFragment
      }
      voucherTenant {
        ...VoucherTenantFragment
      }
    }
  }
`;
export const TypedVoucherTenantUpdate = TypedMutation<
  VoucherTenantUpdate,
  VoucherTenantUpdateVariables
>(voucherTenantUpdate);

export const updateVoucherGroupStatusMutation = gql`
  mutation UpdateVoucherGroupStatus(
    $ids: [ID!]!
    $status: DiscountStatusEnum!
  ) {
    updateVoucherGroupStatus(ids: $ids, status: $status) {
      voucherJobBulk {
        id
        status
      }
      errors {
        field
        message
      }
      discountErrors {
        field
        message
        code
      }
    }
  }
`;
export const TypedUpdateVoucherGroupStatus = TypedMutation<
  UpdateVoucherGroupStatus,
  UpdateVoucherGroupStatusVariables
>(updateVoucherGroupStatusMutation);

export const VOUCHER_CONDITION_CREATE = gql`
  mutation VoucherConditionCreate(
    $voucherId: ID!
    $voucherConditionsInput: [VoucherConditionInput]!
  ) {
    voucherConditionCreate(
      voucherId: $voucherId
      voucherConditionsInput: $voucherConditionsInput
    ) {
      voucherCondition {
        id
        metric
        operation
        value
      }
    }
  }
`;

export const VOUCHER_CONDITION_UPDATE = gql`
  mutation VoucherConditionUpdate(
    $voucherConditionId: ID!
    $voucherConditionsInput: VoucherConditionInput!
  ) {
    voucherConditionUpdate(
      voucherConditionId: $voucherConditionId
      voucherConditionInput: $voucherConditionsInput
    ) {
      voucherCondition {
        id
        metric
        operation
        value
      }
    }
  }
`;

export const VOUCHER_CONDITION_DELETE = gql`
  mutation VoucherConditionDelete($voucherConditionId: ID!) {
    voucherConditionDelete(voucherConditionId: $voucherConditionId) {
      voucherCondition {
        id
      }
    }
  }
`;
